import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { authAPI } from '../services/api';
import { toast } from 'react-toastify';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const timeoutRef = useRef(null);

  const INACTIVITY_TIMEOUT = 14 * 60 * 1000; // 14 mins to ensure logout before token expiry (15m)

  const resetInactivityTimer = () => {
    if (!isAuthenticated) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      console.log('Inactivity timeout reached, logging out...');
      logout();
      toast.warning('You have been logged out due to inactivity');
    }, INACTIVITY_TIMEOUT);
  };

  useEffect(() => {
    if (!isAuthenticated) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    // Define listener to be removed later
    const handleActivity = () => resetInactivityTimer();

    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    resetInactivityTimer();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isAuthenticated]);

  // Check if user is authenticated on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // Don't check auth if already on login page
    if (window.location.pathname === '/login') {
      setLoading(false);
      return;
    }

    try {
      console.log('Checking authentication session...');
      const response = await authAPI.getProfile();
      console.log('Auth check success:', response.data);
      const userData = response.data.data.user;
      userData.username = userData.fullName || userData.email;
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Auth check failed:', error.response || error);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);

      // Account has 2FA enabled - login is not complete yet
      if (response.data.message === '2FA_REQUIRED') {
        return {
          success: false,
          requires2FA: true,
          userId: response.data.data.userId,
        };
      }

      const { user } = response.data.data;

      user.username = user.fullName || user.email;
      setUser(user);
      setIsAuthenticated(true);

      toast.success(`Welcome back, ${user.username}!`);
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      let message = 'Login failed';

      if (error.response?.data?.message) {
        message = error.response.data.message;
      } else if (error.response?.status === 401) {
        message = 'Invalid email or password';
      } else if (error.response?.status === 403) {
        message = 'Your account has been deactivated. Contact admin.';
      } else if (error.message) {
        message = error.message;
      }

      toast.error(message);
      return { success: false, error: message };
    }
  };

  const verify2FA = async (userId, token) => {
    try {
      const response = await authAPI.verify2FA({ userId, token });
      const { user } = response.data.data;

      user.username = user.fullName || user.email;
      setUser(user);
      setIsAuthenticated(true);

      toast.success(`Welcome back, ${user.username}!`);
      return { success: true };
    } catch (error) {
      console.error('2FA verification error:', error);
      const message = error.response?.data?.message || 'Invalid verification code';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      toast.info('Logged out successfully');
    }
  };

  const changePassword = async (data) => {
    try {
      await authAPI.changePassword(data);
      toast.success('Password changed successfully');
      return { success: true };
    } catch (error) {
      console.error('Change password error:', error);
      const message = error.response?.data?.message || 'Failed to change password';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    verify2FA,
    logout,
    changePassword,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};